import { UIIconColors } from '../../components/icon/ui-icon.helpers';
import { UITable } from '../../components/table/ui-table';
import type { ComponentPlugin } from '../../components/ui-element.type';
import { duplicateIndexSetter } from '../../global/helpers';
import { Labels } from '../../global/labels';
import {
    closestByCond,
    createElement,
    dispatchCustomEvent,
} from '../../global/ui-helpers';
import type { TranslationLabels } from '../../types/labels.type';
import { FormWithValidator } from './ui-table-cloneable.type';

/**
 * @memberof SharedComponents
 * @classdesc Class for ui-table cloneable plugin.
 * @alias UITableCloneable
 * @implements {ComponentPlugin}
 * @param {HTMLTableElement} target Target instance.
 * @fires event:row-added
 * @fires event:row-removed
 */
export class UITableCloneable implements ComponentPlugin {
    private readonly target: UITable;

    /**
     * @param {UITable} target
     */
    constructor(target: UITable) {
        this.target = target;
    }

    static readonly ADD_BUTTON_SELECTOR = '.-add';
    static readonly REMOVE_BUTTON_SELECTOR = '.-remove';

    /**
     * @type {TranslationLabels.Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-table-cloneable', {
            addRow: 'Add row',
            removeRow: 'Remove row',
        });
    }

    /**
     * Adds table cell with remove button after the last row.
     * @param {HTMLTableRowElement} tr
     * @returns {HTMLButtonElement}
     */
    addRemoveButton(tr: HTMLTableRowElement): HTMLButtonElement {
        const button = createElement<HTMLButtonElement>({
            tagName: 'button',
            classList: {
                '-remove': true,
                '-iconed': true,
            },
            attributes: {
                type: 'button',
                title:
                    this.target.labelRemove ||
                    UITableCloneable.labels.removeRow,
            },
            children: [
                {
                    tagName: 'ui-icon',
                    attributes: {
                        glyph: 'cross',
                        bgcolor: UIIconColors.ALERT_RED,
                    },
                },
            ],
        })!;
        const newTd = createElement<HTMLTableCellElement>({
            tagName: 'td',
            classList: {
                'col': true,
                '-remove': true,
            },
            children: [button],
        })!;

        const td = tr.querySelector('td:last-child');
        if (td) {
            td.parentNode?.insertBefore(newTd, td.nextSibling);
        }

        button.addEventListener('click', this.removeRowHandler.bind(this));
        return button;
    }

    /**
     * Removes the last cell with remove button.
     * @param {HTMLTableRowElement} tr
     */
    removeRemoveButton(tr: HTMLTableRowElement): void {
        const removeButton = tr.querySelector(
            UITableCloneable.REMOVE_BUTTON_SELECTOR
        );
        if (!removeButton) {
            return;
        }
        const cell = closestByCond(
            removeButton,
            (node) => (node as HTMLElement).localName === 'td'
        );
        if (cell) {
            cell.parentNode?.removeChild(cell);
        }
    }

    /**
     * Inits remove cells if needed.
     */
    initRemovalCells(): void {
        const rows =
            this.target.querySelectorAll<HTMLTableRowElement>('tbody tr');
        for (const row of rows) {
            this.addRemoveButton(row);
        }
    }

    /**
     * Creates add button and appends it to the end of the table.
     */
    createAddButton(): void {
        const div = createElement({
            tagName: 'ui-buttonbar',
            children: [
                {
                    tagName: 'button',
                    attributes: {
                        type: 'button',
                    },
                    classList: {
                        '-add': true,
                        '-iconed': true,
                    },
                    children: [
                        {
                            tagName: 'ui-caption',
                            attributes: {
                                position: 'right',
                            },
                            children: [
                                {
                                    tagName: 'ui-icon',
                                    attributes: {
                                        glyph: 'add',
                                        bgcolor: UIIconColors.TURQUOISE,
                                    },
                                },
                                {
                                    tagName: 'span',
                                    children: [
                                        this.target.labelAdd ||
                                            UITableCloneable.labels.addRow,
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
        });

        const rows = this.target.querySelectorAll('tbody tr');
        if (div && rows.length) {
            this.target.appendChild(div);
        }
    }

    /**
     * Clones the last table row and resets its values.
     * @private
     * @returns {HTMLTableRowElement}
     */
    private cloneRow(): HTMLTableRowElement {
        const tr = this.target.querySelector<HTMLTableRowElement>(
            'tbody tr:not(.-fixed):first-child'
        )!;
        const newTr = this.prepareTemplate(tr);
        this.removeRemoveButton(newTr);
        const button = this.addRemoveButton(newTr);
        button.addEventListener('click', this.removeRowHandler.bind(this));
        return newTr;
    }

    /**
     * Prepares template for cloning. Handles id, name, for attributes.
     * @param {HTMLTableRowElement} tpl
     * @private
     * @returns {HTMLTableRowElement}
     */
    private prepareTemplate(tpl: HTMLTableRowElement): HTMLTableRowElement {
        const clone = tpl.cloneNode(true) as HTMLTableRowElement;

        clone
            .querySelectorAll<HTMLElement>('ui-hint[type="error"]')
            .forEach((hint) => hint.remove());
        clone
            .querySelectorAll<HTMLElement>('.-error')
            .forEach((node) => node.classList.remove('-error'));
        clone
            .querySelectorAll<HTMLInputElement>('input,select,textarea')
            .forEach((elem) => {
                switch (elem.type) {
                    case 'radio':
                    case 'checkbox':
                        elem.checked = false;
                        elem.removeAttribute('checked');
                        break;
                    default:
                        elem.value = '';
                        break;
                }
                elem.removeAttribute('data-validated');
                elem.removeAttribute('data-error-type');
                elem.removeAttribute('aria-invalid');
                elem.removeAttribute('aria-describedby');
            });

        // prepareRow() in ui-table-invert.js will process this row again if !inverted,
        // otherwise labels won't be attached on mobile
        delete clone.dataset.inverted;
        clone.removeAttribute('data-inverted');

        const index = this.getNewRowIndex();
        if (index > 0) {
            ['id', 'for', 'name'].forEach((attrName) => {
                clone
                    .querySelectorAll(`[${attrName}]`)
                    .forEach((node) =>
                        duplicateIndexSetter(node, attrName, index)
                    );
            });
        }
        return clone;
    }

    /**
     * Clones the last table row and resets its values. Appends after
     * the latest row.
     * @param {Event | undefined} e
     */
    addRowHandler(e: Event | undefined): void {
        const newRow = this.cloneRow();
        newRow.dataset.index = String(this.getNewRowIndex());
        const tbody = this.target.table.tBodies[0];
        if (tbody && newRow) {
            tbody.appendChild(newRow);
        }

        const form = this.target.closest<FormWithValidator>('form');
        if (form?.validator) {
            newRow
                .querySelectorAll<HTMLInputElement>('input,select,textarea')
                .forEach((input) =>
                    form.validator?.appendElementToValidation(input)
                );
        }

        newRow.classList.add('-highlight-fade');
        dispatchCustomEvent(this.target, 'row-added', {
            index: this.getNewRowIndex(),
        });
        this.checkAndToggleRemoveButtons();
    }

    /**
     * Function callback for removing rows.
     * @private
     * @param {Event} e
     */
    private removeRowHandler(e: Event): void {
        const tr = closestByCond(
            e.target as HTMLElement,
            (node) => (node as HTMLElement).localName === 'tr'
        ) as HTMLTableRowElement | null;
        const fixedRow = tr?.nextElementSibling;
        if (fixedRow?.classList?.contains('-fixed')) {
            fixedRow.parentNode?.removeChild(fixedRow);
        }
        if (tr?.parentNode) {
            tr.parentNode.removeChild(tr);
        }
        dispatchCustomEvent(this.target, 'row-removed', {
            index: tr?.dataset?.index,
        });
        this.checkAndToggleRemoveButtons();
    }

    /**
     * Handles TH and TD elements visibility. If there aren't any removable rows
     * hide final empty TH cell and in table footer will be added empty cell.
     */
    checkAndToggleRemoveButtons(): void {
        const rows = this.target.querySelectorAll('tbody tr');
        if (!rows.length) {
            return;
        }
        const lastRow = this.target.querySelector<HTMLTableRowElement>(
            'tbody tr:last-child'
        )!;
        const firstRow = this.target.querySelector<HTMLTableRowElement>(
            'tbody tr:first-child'
        )!;
        if (rows.length === 1) {
            this.removeRemoveButton(lastRow);
        }

        const rbtn = firstRow.querySelector(
            UITableCloneable.REMOVE_BUTTON_SELECTOR
        );
        if (rows.length > 1 && !rbtn) {
            this.addRemoveButton(firstRow);
        }
    }

    /**
     * Return new row index.
     * @private
     * @returns {number}
     */
    private getNewRowIndex(): number {
        const tbody = this.target.table.tBodies[0];
        const lastRow = tbody.rows.item(tbody.rows.length - 1);
        return lastRow?.dataset.hasOwnProperty('index')
            ? parseInt(lastRow.dataset.index!) + 1
            : 0;
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.target.table.classList.add('-cloneable');
        this.createAddButton();
        this.initRemovalCells();
        this.checkAndToggleRemoveButtons();

        // Adds empty cells to the end of table head and foot to keep layout.
        const emptyTh: HTMLTableCellElement = (this.target.querySelector(
            '.col.-empty'
        ) ||
            createElement({
                tagName: 'th',
                attributes: {
                    'aria-hidden': 'true',
                },
                classList: {
                    'col': true,
                    '-empty': true,
                },
            }))!;

        const emptyTd: HTMLTableCellElement = (this.target.querySelector(
            '.col.-empty'
        ) ||
            createElement({
                tagName: 'td',
                attributes: {
                    'aria-hidden': 'true',
                },
                classList: {
                    'col': true,
                    '-empty': true,
                },
            }))!;

        const th = this.target.querySelector<HTMLTableCellElement>(
            'thead th:last-child'
        );
        const td = this.target.querySelector<HTMLTableCellElement>(
            'tfoot td:last-child'
        );
        if (th) {
            th.parentNode?.insertBefore(emptyTh, th.nextSibling);
        }
        if (td) {
            td.parentNode?.insertBefore(emptyTd, td.nextSibling);
        }

        this.target
            .querySelectorAll<HTMLTableRowElement>('tbody tr')
            .forEach((row, idx) => {
                row.dataset.index = String(idx);
            });
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        const addRowHandler = this.addRowHandler.bind(this);
        const removeRowHandler = this.removeRowHandler.bind(this);
        this.target
            .querySelectorAll<HTMLElement>(UITableCloneable.ADD_BUTTON_SELECTOR)
            .forEach((btn) => btn.addEventListener('click', addRowHandler));
        this.target
            .querySelectorAll<HTMLElement>(
                UITableCloneable.REMOVE_BUTTON_SELECTOR
            )
            .forEach((btn) => btn.addEventListener('click', removeRowHandler));
    }
}

UITable.registerPlugin('cloneable', UITableCloneable);

/**
 * @memberof UITableCloneable
 * @event row-added
 * @alias event:row-added
 * @type {string}
 * @augments {CustomEvent}
 */

/**
 * @memberof UITableCloneable
 * @event row-removed
 * @alias event:row-removed
 * @type {string}
 * @augments {CustomEvent}
 */
